import { STUDY_STATUS } from '@/entities/study';
import { whiteArrowBackIcon, whiteArrowNextIcon } from '@/shared/ui/assets';

import classNames from 'classnames';
import ImageGallery from 'react-image-gallery';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import { useImageECG } from './useImageECG';
import type { ImageECGProps } from './imageECG.types';

const ImageECG = ({
  stripImgs,
  hasSmallWidth,
  eventId,
  forceRerender,
  isNoEcgData,
  study,
}: ImageECGProps) => {
  const { state, createItems, onImageLoad, onImageError } = useImageECG({
    stripImgs,
    forceRerender,
  });

  /**
   * Renders the previous-image control of the event ECG image gallery. Wraps a white back-arrow image in a button carrying the gallery's disabled state and click handler.
   *
   * @param onClick - Gallery callback moving to the previous image.
   * @param disabled - Whether the control is disabled at the first image.
   * @returns The left navigation button element.
   */
  const renderLeftNav = (onClick: any, disabled: any) => (
    <button
      type="button"
      aria-label={t`Previous image`}
      className="image-gallery-custom-nav left"
      disabled={disabled}
      onClick={onClick}
    >
      <img src={whiteArrowBackIcon} alt="back" />
    </button>
  );

  /**
   * Renders the next-image control of the event ECG image gallery. Wraps a white next-arrow image in a button carrying the gallery's disabled state and click handler.
   *
   * @param onClick - Gallery callback moving to the next image.
   * @param disabled - Whether the control is disabled at the last image.
   * @returns The right navigation button element.
   */
  const renderRightNav = (onClick: any, disabled: any) => (
    <button
      type="button"
      aria-label={t`Next image`}
      className="image-gallery-custom-nav right"
      disabled={disabled}
      onClick={onClick}
    >
      <img src={whiteArrowNextIcon} alt="next" />
    </button>
  );

  return (
    <div
      id={`slider-image-ecg-${eventId}`}
      className={classNames(
        'slider-image-ecg',
        stripImgs?.length ? '--has-default-height' : '',
        hasSmallWidth ? '--has-small-width' : '',
      )}
    >
      {stripImgs?.length ? (
        <>
          <ImageGallery
            items={createItems}
            showFullscreenButton={false}
            showBullets
            lazyLoad
            disableKeyDown
            disableThumbnailScroll
            slideDuration={250}
            startIndex={0}
            showPlayButton={false}
            renderLeftNav={renderLeftNav}
            renderRightNav={renderRightNav}
            onImageLoad={onImageLoad}
            onImageError={onImageError}
            infinite={false}
            thumbnailPosition="bottom"
          />
          {stripImgs?.length > 0 &&
            (state.isLoading ? (
              <div className="loading-thumbnail">
                <Skeleton height="100%" />
              </div>
            ) : (
              state.isLoadedImageError && (
                <div className="loading-thumbnail --error">
                  {t`There is an error while loading ECG strips`}
                </div>
              )
            ))}
        </>
      ) : (
        <div className="no-ecg-strip-container">
          <span className="no-ecg-strip-text">
            {isNoEcgData
              ? study?.status === STUDY_STATUS.DONE
                ? t`No strip is available for this event. This can happen if the patient wasn't wearing the device or it was being charged during the event.`
                : t`The strip is not available yet. The data may still be uploading. Please check again after the study is complete and the device has finished uploading.`
              : t`There is no strip to display`}
          </span>
        </div>
      )}
    </div>
  );
};

export default ImageECG;
